import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { APP_LANGUAGES, type LanguageCode } from '@/shared/config/languages';

interface LanguageTabsProps {
  activeLang: LanguageCode;
  onChange: (lang: LanguageCode) => void;
  className?: string;
}

export const LanguageTabs = ({ activeLang, onChange, className }: LanguageTabsProps) => {
  const { t } = useTranslation();

  // Focus ni saqlash va qayta tiklash funksiyasi
  const changeLanguageWithFocus = (newLang: LanguageCode) => {
    const activeElement = document.activeElement as HTMLInputElement | HTMLTextAreaElement;
    let newFieldId: string | null = null;

    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      const currentId = activeElement.id;

      if (currentId) {
        const langCodes = APP_LANGUAGES.map((l) => l.code);
        const langPattern = new RegExp(`_(${langCodes.join('|')})$`);

        if (langPattern.test(currentId)) {
          newFieldId = currentId.replace(langPattern, `_${newLang}`);
        }
      }
    }

    onChange(newLang);

    if (newFieldId) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          const newField = document.getElementById(newFieldId!) as HTMLInputElement | HTMLTextAreaElement;
          if (newField) {
            newField.focus();
            const length = newField.value?.length || 0;
            newField.setSelectionRange(length, length);
          }
        }, 10);
      });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Alt + ArrowRight yoki Alt + ArrowLeft
      if (e.altKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        e.stopPropagation();

        const currentIndex = APP_LANGUAGES.findIndex((lang) => lang.code === activeLang);
        let nextIndex: number;

        if (e.key === 'ArrowRight') {
          nextIndex = (currentIndex + 1) % APP_LANGUAGES.length;
        } else {
          nextIndex = (currentIndex - 1 + APP_LANGUAGES.length) % APP_LANGUAGES.length;
        }

        changeLanguageWithFocus(APP_LANGUAGES[nextIndex].code);
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [activeLang, onChange]);

  return (
    <Tabs
      className={className}
      activeKey={activeLang}
      onChange={(key) => changeLanguageWithFocus(key as LanguageCode)}
      items={APP_LANGUAGES.map((lang) => ({
        key: lang.code,
        label: t(`languages.${lang.code}`),
      }))}
    />
  );
};
